/**
 * Create/edit form for knowledge base articles.
 */

import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Tabs,
  Space,
  message,
  Row,
  Col,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useKBCategories,
  useKBTags,
  useKBArticles,
  useKBArticle,
  useCreateArticle,
  useUpdateArticle,
} from '../hooks/useKnowledgeBase';
import { getArticleBySlugForValidation } from '../services/knowledgeBase';
import {
  LanguageSettingsControl,
  RichTextEditor,
  RelatedArticlesSelector,
} from '../components/knowledgeBase';
import { DEFAULT_LANGUAGE_SETTINGS, validateLanguageSettings, sanitizeLanguageSettings } from '@municipality/types';
import { AVAILABLE_LANGUAGES } from '@municipality/types';
import type { KBArticleTranslation } from '@municipality/types';

const { TextArea } = Input;

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type ArticleFormData = {
  categoryId: string;
  tagIds: string[];
  relatedArticleIds: string[];
  translations: Record<string, { title: string; summary?: string; content: string }>;
  languageSettings: { primaryLanguage: string; secondaryLanguages: string[]; isMultilingual: boolean };
  slug: string;
  status: 'draft' | 'published';
};

const schema = yup.object({
  categoryId: yup.string().required('Category is required'),
  tagIds: yup.array().of(yup.string()),
  relatedArticleIds: yup.array().of(yup.string()).max(6),
  translations: yup.object().test(
    'primary-title-summary',
    'Title required (max 200 chars). Summary max 500 chars.',
    function (value) {
      const parent = this.parent as ArticleFormData;
      const primaryLang = parent?.languageSettings?.primaryLanguage;
      if (!value || typeof value !== 'object' || !primaryLang) return false;
      const tr = value as Record<string, { title?: string; summary?: string }>;
      const title = tr[primaryLang]?.title?.trim();
      if (!title) return false;
      if (title.length > 200) return false;
      for (const v of Object.values(tr)) {
        if (v?.title && v.title.length > 200) return false;
        if (v?.summary && v.summary.length > 500) return false;
      }
      return true;
    }
  ),
  languageSettings: yup.object({
    primaryLanguage: yup.string().required(),
    secondaryLanguages: yup.array().of(yup.string()).default([]),
    isMultilingual: yup.boolean().default(false),
  }),
  slug: yup
    .string()
    .transform((v) => v?.trim() ?? '')
    .test('slug-format', 'Slug must be lowercase letters, numbers, hyphens only', (v) => !v || SLUG_REGEX.test(v))
    .max(100),
  status: yup.string().oneOf(['draft', 'published']).required(),
});

export const KnowledgeBaseArticleForm: React.FC = () => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;

  const { data: categories } = useKBCategories();
  const { data: tags } = useKBTags();
  const { data: articles } = useKBArticles();
  const { data: article } = useKBArticle(id ?? null);
  const createMutation = useCreateArticle();
  const updateMutation = useUpdateArticle();

  const { control, handleSubmit, watch, setValue, getValues } = useForm<ArticleFormData>({
    resolver: yupResolver(schema) as Resolver<ArticleFormData>,
    defaultValues: {
      categoryId: '',
      tagIds: [],
      relatedArticleIds: [],
      translations: { en: { title: '', summary: '', content: '' } },
      languageSettings: DEFAULT_LANGUAGE_SETTINGS,
      slug: '',
      status: 'draft',
    },
  });

  const languageSettings = watch('languageSettings');
  const translations = watch('translations');

  useEffect(() => {
    if (article) {
      setValue('categoryId', article.categoryId);
      setValue('tagIds', article.tagIds ?? []);
      setValue('relatedArticleIds', article.relatedArticleIds ?? []);
      setValue('translations', article.translations as ArticleFormData['translations']);
      setValue('languageSettings', article.languageSettings);
      setValue('slug', article.slug);
      setValue('status', article.status);
    }
  }, [article, setValue]);

  useEffect(() => {
    const primary = languageSettings?.primaryLanguage ?? 'en';
    const secondary = languageSettings?.secondaryLanguages ?? [];
    const langs = [primary, ...secondary.filter((l) => l !== primary)];
    const prev = getValues('translations') as Record<string, { title: string; summary?: string; content: string }>;
    const next = { ...prev };
    let changed = false;
    langs.forEach((lang) => {
      if (!next[lang]) {
        next[lang] = { title: '', summary: '', content: '' };
        changed = true;
      }
    });
    if (changed) setValue('translations', next);
  }, [languageSettings?.primaryLanguage, languageSettings?.secondaryLanguages, setValue, getValues]);

  const generateSlug = (): void => {
    const primary = languageSettings?.primaryLanguage ?? 'en';
    const title = (translations?.[primary]?.title ?? translations?.en?.title ?? '').trim();
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 100);
    setValue('slug', slug);
  };

  const getTabItems = (): { key: string; label: string; children: React.ReactNode }[] => {
    const primary = languageSettings?.primaryLanguage ?? 'en';
    const secondary = languageSettings?.secondaryLanguages ?? [];
    const langs = [primary, ...secondary.filter((l) => l !== primary)];
    if (langs.length === 0) {
      return [
        {
          key: 'en',
          label: 'English',
          children: (
            <>
              <Controller
                name="translations.en.title"
                control={control}
                render={({ field }) => (
                  <Form.Item label={t('articles.titleField')} required>
                    <Input {...field} onBlur={generateSlug} />
                  </Form.Item>
                )}
              />
              <Controller
                name="translations.en.summary"
                control={control}
                render={({ field }) => (
                  <Form.Item label={t('articles.summary')}>
                    <TextArea {...field} rows={2} />
                  </Form.Item>
                )}
              />
              <Controller
                name="translations.en.content"
                control={control}
                render={({ field }) => (
                  <Form.Item label={t('articles.content')}>
                    <RichTextEditor value={field.value ?? ''} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </>
          ),
        },
      ];
    }
    return langs.map((langCode) => {
      const opt = AVAILABLE_LANGUAGES.find((l) => l.code === langCode);
      return {
        key: langCode,
        label: opt?.nativeName ?? langCode,
        children: (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Controller
              name={`translations.${langCode}.title`}
              control={control}
              render={({ field }) => (
                <Form.Item label={t('articles.titleField')} required>
                  <Input {...field} onBlur={langCode === (primary ?? 'en') ? generateSlug : undefined} />
                </Form.Item>
              )}
            />
            <Controller
              name={`translations.${langCode}.summary`}
              control={control}
              render={({ field }) => (
                <Form.Item label={t('articles.summary')}>
                  <TextArea {...field} rows={2} />
                </Form.Item>
              )}
            />
            <Controller
              name={`translations.${langCode}.content`}
              control={control}
              render={({ field }) => (
                <Form.Item label={t('articles.content')}>
                  <RichTextEditor value={field.value ?? ''} onChange={field.onChange} />
                </Form.Item>
              )}
            />
          </Space>
        ),
      };
    });
  };

  const onSubmit = async (data: ArticleFormData): Promise<void> => {
    try {
      let slug = data.slug?.trim() ?? '';
      if (!slug) {
        const primary = data.languageSettings?.primaryLanguage ?? 'en';
        const title = data.translations?.[primary]?.title ?? data.translations?.en?.title ?? '';
        slug = title
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '')
          .slice(0, 100);
      }
      if (!SLUG_REGEX.test(slug)) {
        message.error('Please enter a valid slug (lowercase letters, numbers, hyphens only)');
        return;
      }

      const existingBySlug = await getArticleBySlugForValidation(
        slug,
        isCreate ? undefined : id ?? undefined
      );
      if (existingBySlug) {
        message.error(t('knowledgeBase:errors.slugTaken'));
        return;
      }

      const validated = validateLanguageSettings(data.languageSettings);
      if (!validated.valid) {
        message.error(validated.errors.join(', '));
        return;
      }
      const langSettings = sanitizeLanguageSettings(data.languageSettings);

      const trans: Record<string, KBArticleTranslation> = {};
      Object.entries(data.translations).forEach(([lang, v]) => {
        if (v?.title?.trim()) {
          trans[lang] = {
            title: v.title.trim(),
            content: (v.content ?? '').trim(),
            summary: v.summary?.trim(),
          };
        }
      });
      if (Object.keys(trans).length === 0) {
        message.error(t('knowledgeBase:errors.saveError'));
        return;
      }

      if (isCreate) {
        const created = await createMutation.mutateAsync({
          categoryId: data.categoryId,
          tagIds: data.tagIds,
          relatedArticleIds: data.relatedArticleIds
          .filter((aid) => aid !== id && (articles ?? []).some((a) => a.id === aid))
          .slice(0, 6),
          translations: trans,
          languageSettings: langSettings,
          slug,
          status: data.status,
        });
        message.success(t('knowledgeBase:messages.articleCreated'));
        navigate(`/knowledge-base/articles/edit/${created.id}`);
      } else if (id) {
        await updateMutation.mutateAsync({
          id,
          data: {
            categoryId: data.categoryId,
            tagIds: data.tagIds,
            relatedArticleIds: data.relatedArticleIds
          .filter((aid) => aid !== id && (articles ?? []).some((a) => a.id === aid))
          .slice(0, 6),
            translations: trans,
            languageSettings: langSettings,
            slug,
            status: data.status,
          },
        });
        message.success(t('knowledgeBase:messages.articleUpdated'));
        navigate('/knowledge-base/articles');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('knowledgeBase:errors.saveError');
      message.error(msg);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const primary = languageSettings?.primaryLanguage ?? 'en';

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/knowledge-base/articles')}>
          {t('common:buttons.back')}
        </Button>
        <h2 style={{ margin: 0 }}>
          {isCreate ? t('articles.create') : t('articles.edit')}
        </h2>
      </div>

      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Card style={{ marginBottom: 24 }}>
          <LanguageSettingsControl control={control} setValue={setValue} />
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <Form.Item label={t('articles.category')} required>
                      <Select
                        {...field}
                        placeholder={t('articles.category')}
                        options={categories?.map((c) => ({
                          label: (c.translations[primary]?.name ?? c.translations.en?.name) || c.id,
                          value: c.id,
                        }))}
                      />
                    </Form.Item>
                  )}
                />
              </Col>
              <Col xs={24} md={12}>
                <Controller
                  name="tagIds"
                  control={control}
                  render={({ field }) => (
                    <Form.Item label={t('articles.tags')}>
                      <Select
                        mode="multiple"
                        {...field}
                        placeholder={t('articles.tags')}
                        options={tags?.map((tagItem) => ({
                          label: (tagItem.translations[primary]?.name ?? tagItem.translations.en?.name) || tagItem.id,
                          value: tagItem.id,
                        }))}
                      />
                    </Form.Item>
                  )}
                />
              </Col>
            </Row>
            <Controller
              name="relatedArticleIds"
              control={control}
              render={({ field }) => (
                <Form.Item label={t('articles.relatedArticles')}>
                  <RelatedArticlesSelector
                    articles={articles ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    excludeArticleId={id}
                    maxCount={6}
                  />
                </Form.Item>
              )}
            />
          </Space>
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <Tabs items={getTabItems()} />
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col xs={24} md={18}>
              <Controller
                name="slug"
                control={control}
                render={({ field }) => (
                  <Form.Item
                    label={t('articles.slug')}
                    extra="Lowercase letters, numbers, hyphens only. Max 100 chars."
                  >
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Form.Item label={t('articles.status')}>
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { label: t('articles.draft'), value: 'draft' },
                        { label: t('articles.published'), value: 'published' },
                      ]}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isPending}>
            {t('common:buttons.save')}
          </Button>
          <Button onClick={() => navigate('/knowledge-base/articles')}>
            {t('common:buttons.cancel')}
          </Button>
        </Space>
      </Form>
    </div>
  );
};
